import { useMutation, useQuery } from "@tanstack/react-query";
import cx from "classnames";
import { useMemo, useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";

import { Button } from "@/atoms/button/Button";
import { GiftCardPreviewImage } from "@/modules/giftCards";
import { resendGiftCardNotification } from "@/modules/giftCards/giftCards.utils";
import { queryClient } from "@/pages/Root";
import { formatCurrency } from "@/toolbox/Money";
import { waitObj } from "@/toolbox/Promise";
import { defineLoader } from "@/toolbox/Router";
import { get } from "api";
import { Notification, useNotify } from "atoms/notification";
import { DescriptiveTag } from "atoms/tag/DescriptiveTag";

type LoaderData = Awaited<ReturnType<typeof giftCardDetailsLoader>>;
export const giftCardDetailsLoader = defineLoader(async () => {
  const sentGiftCards = queryClient.fetchQuery(["/gift-cards/sent"], () =>
    get("/gift-cards/sent", {
      requestedLanguageCode: 0,
    })
  );

  const receivedGiftCards = queryClient.fetchQuery(
    ["/gift-cards/redeemed"],
    () =>
      get("/gift-cards/redeemed", {
        requestedLanguageCode: 0,
      })
  );

  return waitObj({
    sentGiftCards,
    receivedGiftCards,
  });
});

const GiftCardDetailsPage = () => {
  const navigate = useNavigate();
  const [notificationProps, notify] = useNotify();
  const [resendNotification, setResendNotification] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const { sentGiftCards, receivedGiftCards } = useLoaderData() as LoaderData;
  const { giftCardTransferId } = useParams();
  const card = useMemo(() => {
    return (
      sentGiftCards.find(
        (card) => card.giftCardTransferItemId.value === giftCardTransferId
      ) ||
      receivedGiftCards.find(
        (card) => card.giftCardTransferItemId.value === giftCardTransferId
      )
    );
  }, [sentGiftCards, receivedGiftCards, giftCardTransferId]);

  // Fetch full transfer details to get scheduledDeliveryDateUtc
  const { data: fullTransferDetails } = useQuery({
    queryKey: ["/gift-cards/transfer", giftCardTransferId],
    queryFn: () =>
      get("/gift-cards/transfer/{giftCardTransferItemId}", {
        giftCardTransferItemId: giftCardTransferId || "",
      }),
    enabled: !!giftCardTransferId,
  });

  // Use the full transfer details if available, otherwise fall back to the card data
  const cardWithScheduledDate = useMemo(() => {
    if (!card) return null;

    if (fullTransferDetails) {
      return {
        ...card,
        scheduledDeliveryDateUtc: (fullTransferDetails as any)
          .scheduledDeliveryDateUtc,
        platformLogoUrl: (card as any).platformLogoUrl,
      } as any;
    }
    return {
      ...card,
      scheduledDeliveryDateUtc: undefined,
      platformLogoUrl: (card as any).platformLogoUrl,
    } as any;
  }, [card, fullTransferDetails]);

  const resendMutation = useMutation({
    mutationFn: (giftCardTransferItemId: string) =>
      resendGiftCardNotification({ giftCardTransferItemId }),
    onSuccess: async () => {
      setResendNotification({
        type: "success",
        message: `✨ Gift card sent to ${
          cardWithScheduledDate.recipientName.split(" ")[0]
        }`,
      });
      setIsNotificationVisible(true);
      await queryClient.invalidateQueries({ queryKey: ["/gift-cards/sent"] });
      // Start fade out after 3 seconds
      setTimeout(() => {
        setIsNotificationVisible(false);
        // Clear notification after fade animation completes
        setTimeout(() => {
          setResendNotification({ type: null, message: "" });
        }, 300);
      }, 3000);
    },
    onError: () => {
      setResendNotification({
        type: "error",
        message: `Failed to send gift card notification to ${
          cardWithScheduledDate.recipientName.split(" ")[0]
        }. Please try again.`,
      });
      setIsNotificationVisible(true);
      // Start fade out after 4 seconds
      setTimeout(() => {
        setIsNotificationVisible(false);
        // Clear notification after fade animation completes
        setTimeout(() => {
          setResendNotification({ type: null, message: "" });
        }, 300);
      }, 4000);
    },
  });

  if (!cardWithScheduledDate) {
    return (
      <div className="flex h-full flex-col bg-one">
        <h1 className="text-title">Gift Cards</h1>

        <div className="flex grow items-center justify-center px-6 text-center">
          <div className="space-y-4">
            <div className="text-bold2 text-primary">Gift card not found</div>
            <Button onClick={() => navigate("/wallet/giftcards")}>
              Back to Gift Cards
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const amountLabel = formatCurrency(cardWithScheduledDate.giftCardValue, 0);

  const buttonText =
    cardWithScheduledDate.ownershipType === "Receiver"
      ? "Return the favor"
      : cardWithScheduledDate.ownershipType === "Sender"
      ? "Send another one"
      : "Buy another gift card";

  const handleGoToGiftCardInShop = () => {
    if (cardWithScheduledDate.ownershipType === "Myself") {
      navigate("/shop/gift-cards?ownershipType=Myself");
      return;
    }

    const searchParams = new URLSearchParams({
      name:
        cardWithScheduledDate.ownershipType === "Receiver"
          ? cardWithScheduledDate.senderName
          : cardWithScheduledDate.recipientName,
      ownershipType: cardWithScheduledDate.ownershipType ?? "Myself",
      phoneNumber:
        cardWithScheduledDate.ownershipType === "Receiver"
          ? cardWithScheduledDate.senderPhoneNumber ?? ""
          : cardWithScheduledDate.ownershipType === "Sender"
          ? cardWithScheduledDate.recipientPhoneNumber ?? ""
          : "",
    });

    navigate(`/shop/gift-cards?${searchParams.toString()}`);
  };

  const handleResend = async () => {
    await resendMutation.mutateAsync(
      cardWithScheduledDate.giftCardTransferItemId.value
    );
  };

  return (
    <div className="relative flex flex-grow flex-col justify-between">
      <Notification
        {...notificationProps}
        state={
          notificationProps.state && {
            ...notificationProps.state,
          }
        }
      />
      <div className="flex grow flex-col gap-6 pb-6 pt-6">
        <div className="rounded-xl p-4 px-8">
          <div className="mb-3 h-48 w-full overflow-hidden rounded-md border">
            <GiftCardPreviewImage
              backgroundImage={cardWithScheduledDate.imageUrl}
              size="large"
              amount={amountLabel.replace("$", "")}
              platformLogoUrl={cardWithScheduledDate.platformLogoUrl}
            />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md bg-white p-4 shadow-subtle">
            {cardWithScheduledDate.ownershipType !== "Myself" && (
              <div>
                <p className="text-body2 text-primary">
                  To: {cardWithScheduledDate.recipientName}
                </p>
                <p className="text-body2 text-primary">
                  From: {cardWithScheduledDate.senderName}
                </p>
              </div>
            )}
            <div
              className={cx(
                "text-h1 text-primary",
                cardWithScheduledDate.ownershipType === "Myself" && "mx-auto"
              )}
            >
              {amountLabel}
            </div>
          </div>
        </div>

        {!!cardWithScheduledDate.message && (
          <div className="px-8 text-center">
            <h2
              className={`font-['STIX_Two_Text'] italic leading-relaxed text-primary ${
                cardWithScheduledDate.message.length > 100
                  ? "text-body1"
                  : cardWithScheduledDate.message.length > 50
                  ? "text-bold1"
                  : "text-h2"
              }`}
            >
              &quot;{cardWithScheduledDate.message}&quot;
            </h2>
          </div>
        )}
        {cardWithScheduledDate.ownershipType === "Sender" &&
          (() => {
            // If no scheduled date, show immediately (immediate delivery)
            if (!cardWithScheduledDate.scheduledDeliveryDateUtc) {
              return true;
            }
            // If scheduled date exists, only show if it's in the past
            const scheduledDate = new Date(
              cardWithScheduledDate.scheduledDeliveryDateUtc
            );
            const now = new Date();
            const isPast = scheduledDate <= now;
            return isPast;
          })() && (
            <div className="mt-2 flex flex-col items-center gap-2">
              <button type="button" onClick={handleResend}>
                <DescriptiveTag color="secondary" size="large">
                  {`Resend to ${
                    cardWithScheduledDate.recipientName.split(" ")[0]
                  }`}
                </DescriptiveTag>
              </button>
              {resendNotification.type && (
                <p
                  className={`m-0 text-body1 transition-opacity duration-300 ${
                    isNotificationVisible ? "opacity-100" : "opacity-0"
                  } ${
                    resendNotification.type === "success"
                      ? "text-brand-color"
                      : "text-red-600"
                  }`}
                >
                  {resendNotification.message}
                </p>
              )}
            </div>
          )}
      </div>

      <div className="sticky bottom-[env(safe-area-inset-bottom)] w-full bg-one p-4 pb-5">
        <Button fullWidth onClick={handleGoToGiftCardInShop}>
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

export default GiftCardDetailsPage;
